import React, { createContext, useEffect, useState, useMemo } from "react";
import { marketplaceTipOptions } from "components/Payments/config";

export const initialTData = {
  business: [],
  tips: {},
  selectedTip: null,
  couponCode: "",
  isPaid: false,
  charges: null,
  aggregatedTipOptions: marketplaceTipOptions,
  orderType: "",
  isDriver: false,
  selectedDriverTip: null,
  cashPaid: "0.00",
  actionInProgress: false,
  setActionInProgress: () => {},
  merchantSettings: null,
  setMerchantSettings: () => {},
  paidTip: 0,
  setPaidTip: () => {},
};

const TOContext = createContext(initialTData);

const TOContextProvider = ({ children }) => {
  const searchParams = new URLSearchParams(window.location.search);
  const oId = searchParams.get("oId");
  // Constants
  const CREDIT_CARD = "credit card";
  const CASH = "cash";

  const [actionInProgress, setActionInProgress] = useState(false);

  // State management
  const [business, setBusiness] = useState(initialTData.business);
  const [tips, setTips] = useState(initialTData.tips);
  const [selectedTip, setSelectedTip] = useState(initialTData.selectedTip);
  const [selectedDriverTip, setSelectedDriverTip] = useState(
    initialTData.selectedDriverTip
  );
  const [couponCode, setCouponCode] = useState(initialTData.couponCode);
  const [charges, setCharges] = useState(initialTData.charges);
  const [isPaid, setIsPaid] = useState(initialTData.isPaid);
  const [orderType, setOrderType] = useState(initialTData.orderType);
  const [isDriver, setIsDriver] = useState(initialTData.isDriver);
  const [cashPaid, setCashPaid] = useState(initialTData.cashPaid);
  const [aggregatedTipOptions, setAggregatedTipOptions] = useState(
    initialTData.aggregatedTipOptions
  );
  const [payWithCreditCard, setPayWithCreditCard] = useState("");
  const [merchantSettings, setMerchantSettings] = useState(
    initialTData.merchantSettings
  );
  const [paidTip, setPaidTip] = useState(initialTData.paidTip);

  // Derived state
  const isRefundDueShow = charges?.balanceDue < 0;

  // Handle localStorage cleanup on page unload
  useEffect(() => {
    const handleBeforeUnload = () =>
      localStorage.removeItem(`orderNumber-${oId}`);
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // Update cash paid amount when charges or payment method changes
  useEffect(() => {
    if (charges?.balanceDue !== undefined) {
      const balanceDueFormatted = charges.balanceDue.toFixed(2);
      // Only auto-update cashPaid when using cash or when charges change initially
      if (payWithCreditCard === CASH) {
        setCashPaid(balanceDueFormatted);
      }
    }
  }, [charges?.balanceDue, payWithCreditCard, CASH]);

  const handleSelect = (paymentType) =>
    setPayWithCreditCard(paymentType || CREDIT_CARD);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      business,
      setBusiness,
      tips,
      setTips,
      selectedTip,
      setSelectedTip,
      couponCode,
      setCouponCode,
      payWithCreditCard,
      handleSelect,
      CREDIT_CARD,
      CASH,
      isPaid,
      setIsPaid,
      charges,
      setCharges,
      orderType,
      setOrderType,
      aggregatedTipOptions,
      setAggregatedTipOptions,
      isDriver,
      setIsDriver,
      isRefundDueShow,
      selectedDriverTip,
      setSelectedDriverTip,
      cashPaid,
      setCashPaid,
      actionInProgress,
      setActionInProgress,
      merchantSettings,
      setMerchantSettings,
      setPaidTip,
      paidTip,
    }),
    [
      business,
      tips,
      selectedTip,
      couponCode,
      payWithCreditCard,
      isPaid,
      charges,
      orderType,
      aggregatedTipOptions,
      isDriver,
      isRefundDueShow,
      selectedDriverTip,
      cashPaid,
      actionInProgress,
      merchantSettings,
      setMerchantSettings,
      paidTip,
      setPaidTip,
    ]
  );

  return (
    <TOContext.Provider value={contextValue}>{children}</TOContext.Provider>
  );
};

const TOContextConsumer = ({ children }) => {
  return <TOContext.Consumer>{children}</TOContext.Consumer>;
};

export { TOContext, TOContextProvider, TOContextConsumer };
